// DrawdownChart.js

import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import i18n from '../../../i18n';
import './DrawdownChart.css';
import { timelineColors } from './palette'; // Ensure correct path to palette.js

// -------------------- Helper Functions -------------------- //

// Calculate cumulative sum for Rate of Return (ROR)
const cumSum = (levels) => {
  let total = 0;
  return levels.map((level) => {
    total += level * 100; // Convert to percentage
    return parseFloat(total.toFixed(2)); // 2 decimal precision
  });
};

// Calculate growth based on a starting amount (e.g., $10,000)
const growth10k = (levels, initialAmount = 10000) => {
  let total = initialAmount;
  return levels.map((level, index) => {
    if (index === 0) return total;
    total *= 1 + level; // Assuming level is in decimal form
    return Math.round(total / 100) * 100; // Round to the nearest hundred
  });
};

// Compute drawdown from cumulative data (difference from the running peak)
const computeDrawdown = (cumulativeData) => {
  let peak = cumulativeData[0];
  return cumulativeData.map((val) => {
    if (val > peak) {
      peak = val;
    }
    return parseFloat((val - peak).toFixed(2));
  });
};

// Parse both "YYYY-MM-DD" and "MM/DD/YYYY" as a local date
const parseLocalDate = (dateString) => {
  if (dateString.includes('-')) {
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  } else if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(NaN);
};

// Parse "MM/DD/YYYY" as a local date
const parseInputDate = (dateString) => {
  const [month, day, year] = dateString.split('/').map(Number);
  if (!month || !day || !year) return new Date(NaN);
  return new Date(year, month - 1, day);
};

// Format dates as "MM/DD/YYYY"
const formatDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Get start and end dates from data (from the first fund that has dates)
function getStartAndEndDates(data) {
  let startDate = null;
  let endDate = null;
  for (let i = 0; i < data?.length; i++) {
    if (data[i]?.levelsDaily?.date?.length > 0) {
      startDate = parseLocalDate(data[i].levelsDaily.date[0]);
      endDate = parseLocalDate(
        data[i].levelsDaily.date[data[i].levelsDaily.date.length - 1]
      );
      break;
    }
  }
  return { startDate, endDate };
}

// Format data for tooltips
const dataFormatter = (value, chartType) => {
  if (chartType === 'ror') {
    return `${value}%`;
  } else if (chartType === 'growth10k') {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return value;
};

// Process and prepare chart data based on chartType and drawdown mode
function chartData(data, chartType, isDrawdown = false, windowIndices = null) {
  const dataField = 'dlyRoR';
  const dateField = 'date';

  // Process series for each fund.
  // Funds that do not return valid data for the selected window are skipped.
  const series = data
    .map((fund, index) => {
      const fullLevels = fund?.levelsDaily?.[dataField] || [];
      let levels = fullLevels;
      if (windowIndices) {
        levels = fullLevels.slice(
          windowIndices.startIndex,
          windowIndices.endIndex + 1
        );
      }
      if (!levels || levels.length === 0) return null;
      if (chartType === 'ror') {
        // Ensure first level is 0 for ROR
        levels[0] = 0.0;
      }
      let processedData = [];
      if (chartType === 'growth10k') {
        processedData = growth10k(levels);
      } else if (chartType === 'ror') {
        const cumulativeData = cumSum(levels);
        processedData = isDrawdown
          ? computeDrawdown(cumulativeData)
          : cumulativeData;
      }
      const color = timelineColors[index % timelineColors.length];
      return {
        name: `${fund.ticker} - ${fund.name}`,
        data: processedData,
        type: 'line',
        smooth: true,
        lineStyle: { color },
        itemStyle: { color },
        emphasis: { itemStyle: { color } },
      };
    })
    .filter((s) => s !== null);

  // Build legend from only those funds with valid series
  const legendData = series.map((s) => s.name);

  // Prepare xAxis dates (using the first fund’s dates)
  let chartDates = data?.[0]?.levelsDaily?.[dateField] || [];
  if (windowIndices) {
    chartDates = chartDates.slice(
      windowIndices.startIndex,
      windowIndices.endIndex + 1
    );
  }
  const formattedDates = chartDates.map((dateStr) =>
    formatDate(parseLocalDate(dateStr))
  );

  // Determine yAxis configuration based on chart type and mode
  let yAxisConfig = {};
  if (chartType === 'growth10k') {
    let maxVal = 0;
    let minVal = Infinity;
    series.forEach((s) => {
      const sMax = Math.max(...s.data);
      const sMin = Math.min(...s.data);
      if (sMax > maxVal) maxVal = sMax;
      if (sMin < minVal) minVal = sMin;
    });
    const yAxisMax = Math.ceil(maxVal / 1000) * 1000;
    const yAxisMin = minVal < 0 ? Math.floor(minVal / 1000) * 1000 : 0;
    yAxisConfig = {
      type: 'value',
      min: yAxisMin,
      max: yAxisMax,
      interval: Math.ceil((yAxisMax - yAxisMin) / 10),
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        formatter: (value) => `$${(value / 1000).toFixed(0)}K`,
      },
      axisLine: { lineStyle: { color: '#050139' } },
      axisTick: { lineStyle: { color: '#050139' } },
    };
  } else if (chartType === 'ror') {
    if (isDrawdown) {
      let minVal = Infinity;
      series.forEach((s) => {
        const sMin = Math.min(...s.data);
        if (sMin < minVal) minVal = sMin;
      });
      const yAxisMin = Math.floor(minVal / 10) * 10;
      yAxisConfig = {
        type: 'value',
        min: yAxisMin,
        max: 0,
        interval: Math.ceil((0 - yAxisMin) / 10),
        axisLabel: {
          fontSize: 12,
          color: '#050139',
          formatter: (value) => `${value}%`,
        },
        axisLine: { lineStyle: { color: '#050139' } },
        axisTick: { lineStyle: { color: '#050139' } },
        symbol: 'none',
      };
    } else {
      let maxVal = 0;
      let minVal = Infinity;
      series.forEach((s) => {
        const sMax = Math.max(...s.data);
        const sMin = Math.min(...s.data);
        if (sMax > maxVal) maxVal = sMax;
        if (sMin < minVal) minVal = sMin;
      });
      const yAxisMax = Math.ceil(maxVal / 10) * 10;
      const yAxisMin = minVal < 0 ? Math.floor(minVal / 10) * 10 : 0;
      yAxisConfig = {
        type: 'value',
        min: yAxisMin,
        max: yAxisMax,
        interval: Math.ceil((yAxisMax - yAxisMin) / 10),
        axisLabel: {
          fontSize: 12,
          color: '#050139',
          formatter: '{value}%',
        },
        axisLine: { lineStyle: { color: '#050139' } },
        axisTick: { lineStyle: { color: '#050139' } },
        symbol: 'none',
      };
    }
  }

  return {
    // You can uncomment and update the title if needed.
    // title: {
    //   text:
    //     chartType === 'ror'
    //       ? isDrawdown
    //         ? 'Drawdown'
    //         : 'Cumulative Total Return'
    //       : 'Growth of 10K',
    //   left: 5,
    //   textStyle: {
    //     fontStyle: 'normal',
    //     fontWeight: 900,
    //     fontSize: 14,
    //     color: '#8C959B',
    //   },
    // },
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5,
      top: 10,
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let res = `${params[0].name}<br/>`;
        params.forEach((param) => {
          if (param.value !== null && param.value !== undefined) {
            res += `${param.marker} ${param.seriesName} : ${dataFormatter(
              param.value,
              chartType
            )}<br/>`;
          }
        });
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: { color: '#5B6770', align: 'left' },
    },
    grid: {
      top: 110,
      left: '3%',
      right: '3%',
      bottom: '5%',
      containLabel: true,
    },
    legend: {
      icon: 'circle',
      itemGap: 20,
      data: legendData,
    },
    dataZoom: [
      { type: 'inside', start: 0, end: 100 },
      {
        type: 'slider',
        start: 0,
        end: 100,
        handleSize: '100%',
        handleStyle: { color: '#050139' },
        showDetail: false,
      },
    ],
    xAxis: {
      type: 'category',
      data: formattedDates,
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        rotate: 0,
        formatter: (value) => value,
      },
      axisLine: { lineStyle: { color: '#050139' } },
      axisTick: { lineStyle: { color: '#050139' } },
    },
    yAxis: yAxisConfig,
    series: series,
  };
}

// -------------------- React Component -------------------- //

class DrawdownChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartType: { value: 'ror', label: 'Rate of Return (ROR)' },
      startDate: null,
      endDate: null,
      chartOptions: {},
      fullXAxisData: [],
      inputStartDate: '',
      inputEndDate: '',
      error: '',
      dateWindowOptions: [],
      selectedDateWindow: null,
      windowIndices: null,
    };
    this.handleDataZoom = this.handleDataZoom.bind(this);
  }

  componentDidMount() {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available.' });
      return;
    }

    const { startDate, endDate } = getStartAndEndDates(data);
    const chartOptions = chartData(data, this.state.chartType.value);
    const fullXAxisData = data?.[0]?.levelsDaily?.date || [];
    const parsedFullDates = fullXAxisData.map((dateStr) =>
      parseLocalDate(dateStr)
    );
    const formattedFullDates = parsedFullDates.map((date) => formatDate(date));

    let drawDownDetails = data?.[0]?.drawDownDetails || [];
    if (!Array.isArray(drawDownDetails)) {
      drawDownDetails = [drawDownDetails];
    }
    const dateWindowOptions = drawDownDetails
      .map((window, index) => {
        if (!window || !window.startDate || !window.endDate) {
          console.warn(
            `DrawDownDetails window at index ${index} is missing startDate or endDate.`
          );
          return null;
        }
        let startDateObj = parseLocalDate(window.startDate);
        let endDateObj = parseLocalDate(window.endDate);
        if (startDateObj > endDateObj) {
          console.warn(
            `DrawDownDetails window at index ${index} has startDate > endDate. Swapping dates.`
          );
          [startDateObj, endDateObj] = [endDateObj, startDateObj];
        }
        return {
          value: index, // preserve original order
          label: `${formatDate(startDateObj)} - ${formatDate(endDateObj)}`,
          startDate: formatDate(startDateObj),
          endDate: formatDate(endDateObj),
        };
      })
      .filter((option) => option !== null)
      .sort((a, b) => a.value - b.value);

    this.setState({
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      chartOptions,
      fullXAxisData: formattedFullDates,
      dateWindowOptions,
    });
  }

  // Helper: Find closest date
  findClosestDate = (targetDate, parsedFullDates, type) => {
    if (!(targetDate instanceof Date) || isNaN(targetDate)) return null;
    if (type === 'start') {
      for (let date of parsedFullDates) {
        if (date >= targetDate) return formatDate(date);
      }
      return formatDate(parsedFullDates[parsedFullDates.length - 1]);
    } else if (type === 'end') {
      for (let i = parsedFullDates.length - 1; i >= 0; i--) {
        if (parsedFullDates[i] <= targetDate)
          return formatDate(parsedFullDates[i]);
      }
      return formatDate(parsedFullDates[0]);
    }
    return null;
  };

  // Handler for chart type change
  handleChartTypeChange = (chartType) => {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available to change chart type.' });
      return;
    }
    const { startDate, endDate } = getStartAndEndDates(data);
    const isDrawdown =
      chartType.value === 'ror' && this.state.selectedDateWindow;
    const chartOptions = chartData(
      data,
      chartType.value,
      isDrawdown,
      this.state.windowIndices
    );
    const fullXAxisData = data?.[0]?.levelsDaily?.date || [];
    const parsedFullDates = fullXAxisData
      .map((dateStr) => parseLocalDate(dateStr))
      .sort((a, b) => a - b);
    const formattedFullDates = parsedFullDates.map((date) => formatDate(date));

    this.setState({
      chartType,
      chartOptions,
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      fullXAxisData: formattedFullDates,
      selectedDateWindow: null,
      windowIndices: null,
      error: '',
    });
  };

  // Handler for date window selection change
  handleDateWindowChange = (selectedOption) => {
    if (!selectedOption) {
      this.resetDateWindow();
      return;
    }

    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available to select date window.' });
      return;
    }

    // Get the start and end dates from the selected option
    const { startDate: optionStartDate, endDate: optionEndDate } =
      selectedOption;

    // Convert the option dates to Date objects
    const optionStartDateObj = parseLocalDate(optionStartDate);
    const optionEndDateObj = parseLocalDate(optionEndDate);

    // Get parsed full dates from state
    const parsedFullDates = this.state.fullXAxisData.map((dateStr) =>
      parseLocalDate(dateStr)
    );

    // Find the closest matching dates using the helper function
    const closestStartDateStr = this.findClosestDate(
      optionStartDateObj,
      parsedFullDates,
      'start'
    );
    const closestEndDateStr = this.findClosestDate(
      optionEndDateObj,
      parsedFullDates,
      'end'
    );

    // Find the indices in fullXAxisData based on the closest date strings
    const startIndex = this.state.fullXAxisData.findIndex(
      (date) => date === closestStartDateStr
    );
    const endIndex = this.state.fullXAxisData.findIndex(
      (date) => date === closestEndDateStr
    );

    if (startIndex === -1 || endIndex === -1) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }

    // Recalculate chart options
    const isDrawdown = this.state.chartType.value === 'ror';
    const chartOptions = chartData(
      data,
      this.state.chartType.value,
      isDrawdown,
      { startIndex, endIndex }
    );

    // Dispatch dataZoom action
    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: Math.max(
        0,
        Math.min(
          (startIndex / (this.state.fullXAxisData.length - 1)) * 100,
          100
        )
      ),
      end: Math.min(
        100,
        Math.max((endIndex / (this.state.fullXAxisData.length - 1)) * 100, 0)
      ),
    });

    // Update state with the closest dates and new chart options
    this.setState({
      startDate: parseLocalDate(closestStartDateStr),
      endDate: parseLocalDate(closestEndDateStr),
      inputStartDate: closestStartDateStr,
      inputEndDate: closestEndDateStr,
      selectedDateWindow: selectedOption,
      windowIndices: { startIndex, endIndex },
      chartOptions,
      error: '',
    });
  };

  // Reset the date window to show all data
  resetDateWindow = () => {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available.' });
      return;
    }
    const { startDate, endDate } = getStartAndEndDates(data);
    const chartOptions = chartData(data, this.state.chartType.value);
    const fullXAxisData = data[0].levelsDaily.date || [];
    const parsedFullDates = fullXAxisData.map((dateStr) =>
      parseLocalDate(dateStr)
    );
    const formattedFullDates = parsedFullDates.map((date) => formatDate(date));
    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: 0,
      end: 100,
    });
    this.setState({
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      chartOptions,
      fullXAxisData: formattedFullDates,
      selectedDateWindow: null,
      windowIndices: null,
      error: '',
    });
  };

  // Handler for manual date input changes
  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      selectedDateWindow: null,
      windowIndices: null,
      error: '',
    });
  };

  // Handler for the "GO" button (manual date input)
  handleGoClick = () => {
    const { inputStartDate, inputEndDate, fullXAxisData, chartType, data } =
      this.state;
    const parsedStartDate = parseInputDate(inputStartDate);
    const parsedEndDate = parseInputDate(inputEndDate);
    if (isNaN(parsedStartDate) || isNaN(parsedEndDate)) {
      this.setState({
        error: 'Please enter valid dates in MM/DD/YYYY format.',
      });
      return;
    }
    if (parsedStartDate > parsedEndDate) {
      this.setState({ error: 'Start date must be before End date.' });
      return;
    }
    const parsedLevelsDates = this.props.data[0].levelsDaily.date.map((d) =>
      parseLocalDate(d)
    );
    const closestStartDateStr = this.findClosestDate(
      parsedStartDate,
      parsedLevelsDates,
      'start'
    );
    const closestEndDateStr = this.findClosestDate(
      parsedEndDate,
      parsedLevelsDates,
      'end'
    );
    if (!closestStartDateStr || !closestEndDateStr) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }
    const startIndex = fullXAxisData.findIndex(
      (date) => date === closestStartDateStr
    );
    const endIndex = fullXAxisData.findIndex(
      (date) => date === closestEndDateStr
    );
    if (startIndex === -1 || endIndex === -1) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }
    const chartOptions = chartData(
      data,
      chartType.value,
      chartType.value === 'ror',
      { startIndex, endIndex }
    );
    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: Math.max(
        0,
        Math.min((startIndex / (fullXAxisData.length - 1)) * 100, 100)
      ),
      end: Math.min(
        100,
        Math.max((endIndex / (fullXAxisData.length - 1)) * 100, 0)
      ),
    });
    this.setState({
      startDate: parseLocalDate(closestStartDateStr),
      endDate: parseLocalDate(closestEndDateStr),
      inputStartDate: closestStartDateStr,
      inputEndDate: closestEndDateStr,
      selectedDateWindow: null,
      windowIndices: { startIndex, endIndex },
      chartOptions,
      error: '',
    });
  };

  // Handler for dataZoom events
  handleDataZoom = () => {
    const echartsInstance = this.echartsRef.getEchartsInstance();
    const option = echartsInstance.getOption();
    if (!option.dataZoom || option.dataZoom.length === 0) return;
    const dataZoom = option.dataZoom[0];
    const start = dataZoom.start;
    const end = dataZoom.end;
    const { fullXAxisData } = this.state;
    const totalDates = fullXAxisData.length;
    const startIndex = Math.floor((start / 100) * (totalDates - 1));
    const endIndex = Math.floor((end / 100) * (totalDates - 1));
    const newStartDateStr = fullXAxisData[startIndex];
    const newEndDateStr = fullXAxisData[endIndex];
    const newStartDate = parseLocalDate(newStartDateStr);
    const newEndDate = parseLocalDate(newEndDateStr);
    if (!isNaN(newStartDate.getTime()) && !isNaN(newEndDate.getTime())) {
      this.setState({
        inputStartDate: formatDate(newStartDate),
        inputEndDate: formatDate(newEndDate),
        startDate: newStartDate,
        endDate: newEndDate,
        selectedDateWindow: null,
        windowIndices: null,
        error: '',
      });
    } else {
      console.error(
        'Invalid Dates after dataZoom:',
        newStartDateStr,
        newEndDateStr
      );
    }
  };

  render() {
    const {
      chartType,
      chartOptions,
      inputStartDate,
      inputEndDate,
      startDate,
      endDate,
      error,
      dateWindowOptions,
      selectedDateWindow,
    } = this.state;

    // Update header title based on drawdown mode:
    // When chart type is 'ror' and a date window is selected, use the drawdown title.
    const headerTitle =
      chartType.value === 'ror' && selectedDateWindow
        ? 'Drawdown'
        : chartType.value === 'ror'
        ? 'Cumulative Total Return'
        : chartType.label;

    return (
      <div>
        <div className="chartHeader">{i18n.t('Drawdowns')}</div>
        <div
          className="chartSubHeader"
          style={{
            textAlign: 'center',
            marginBottom: '20px',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          {headerTitle} - {startDate ? formatDate(startDate) : ''} to{' '}
          {endDate ? formatDate(endDate) : ''}
        </div>
        <div
          className="row"
          style={{ marginBottom: '20px', alignItems: 'center' }}
        >
          <div
            className="col-md-3"
            style={{
              textAlign: 'center',
              paddingRight: '3px',
              borderRadius: '0px',
            }}
          >
            <Select
              options={[
                { value: 'ror', label: 'Rate of Return (ROR)' },
                { value: 'growth10k', label: 'Growth of 10K' },
              ]}
              value={chartType}
              onChange={this.handleChartTypeChange}
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}
              placeholder="Select Chart Type"
            />
          </div>
          <div
            className="col-md-3"
            style={{
              textAlign: 'center',
              paddingLeft: '3px',
              paddingRight: '3px',
            }}
          >
            <Select
              options={dateWindowOptions}
              value={selectedDateWindow}
              onChange={this.handleDateWindowChange}
              isClearable={true}
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}
              placeholder="Select Date Window"
            />
          </div>
          <div
            className="col-md-6"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <div
              className="fund-char-perf date-picker-container"
              style={{ marginRight: '10px' }}
            >
              <input
                type="text"
                name="inputStartDate"
                value={inputStartDate}
                onChange={this.handleInputChange}
                placeholder="Start Date (MM/DD/YYYY)"
                className="form-control"
                style={{
                  textAlign: 'center',
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '38px',
                  borderRadius: '0px',
                  width: '200px',
                }}
              />
            </div>
            <div
              className="fund-char-perf date-picker-container"
              style={{ marginRight: '10px' }}
            >
              <input
                type="text"
                name="inputEndDate"
                value={inputEndDate}
                onChange={this.handleInputChange}
                placeholder="End Date (MM/DD/YYYY)"
                className="form-control"
                style={{
                  textAlign: 'center',
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '38px',
                  borderRadius: '0px',
                  width: '200px',
                }}
              />
            </div>
            <button
              className="dlyPerfbutton dlyPerfGoButton"
              onClick={this.handleGoClick}
              style={{ height: '38px' }}
            >
              GO
            </button>
          </div>
        </div>
        {error && (
          <div className="row" style={{ marginBottom: '10px' }}>
            <div
              className="col-md-12"
              style={{ color: 'red', textAlign: 'center' }}
            >
              {error}
            </div>
          </div>
        )}
        <div className="chartContainer">
          <ReactEcharts
            option={chartOptions}
            ref={(e) => (this.echartsRef = e)}
            theme="default"
            notMerge={true}
            style={{ height: 500 }}
            onEvents={{ dataZoom: this.handleDataZoom }}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return { data: state.fundData.data };
}

export default connect(mapStateToProps, {})(DrawdownChart);
